import { useState } from 'react';

import { Button } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { useToast } from '../../../lib/toast';
import { NganGanVaiTro } from '../../vai-tro/components/NganGanVaiTro';
import { FormMoiNguoiDung } from '../components/FormMoiNguoiDung';
import { FormSuaNguoiDung } from '../components/FormSuaNguoiDung';
import { HopBatBuocMfa } from '../components/HopBatBuocMfa';
import { HopDoiEmailNhanThu } from '../components/HopDoiEmailNhanThu';
import { HopKhoaNguoiDung } from '../components/HopKhoaNguoiDung';
import { HopXacNhanThaoTac } from '../components/HopXacNhanThaoTac';
import { KHOA_LOI_DAT_LAI_MAT_KHAU } from '../constants';
import { noiNhanThu, phanLoaiLoiDatLai } from '../nghiep-vu/thao-tac';
import type { InvitedUser, UserProfile } from '../types';
import {
  useBatBuocMfaNguoiDung,
  useDatLaiMfa,
  useDoiEmailNhanThu,
  useGuiDatLaiMatKhau,
  useGanVaiTroNguoiMoi,
  useGuiLaiLoiMoi,
  useKhoaNguoiDung,
  useMoKhoaDangNhap,
  useMoiNguoiDung,
  useSuaNguoiDung,
} from './useNguoiDung';

/**
 * Hộp thoại và mutation của các thao tác trên người dùng — mời, sửa, khoá, mở
 * khoá, gửi lại lời mời, bắt buộc hay bỏ bắt buộc xác thực hai lớp, gán vai trò lớp 2 (ngăn của phân hệ `vai-tro`),
 * và lấy lại tài khoản (email đặt lại mật khẩu, mở khoá đăng nhập, đặt lại xác thực hai lớp). Trả hàm mở từng việc và `hopThoai` để view đặt vào
 * cây, cùng khuôn `useThaoTacTenant`.
 *
 * Mỗi lần mở tăng `lan`, dùng làm `key` của form: form dựng lại sạch — kể cả
 * `Idempotency-Key` mới của hộp mời — mà không cần effect đồng bộ.
 */
export function useThaoTacNguoiDung() {
  const toast = useToast();
  const dichLoi = useLoi();
  const moi = useMoiNguoiDung();
  const ganLai = useGanVaiTroNguoiMoi();
  const sua = useSuaNguoiDung();
  const khoa = useKhoaNguoiDung();
  const guiLai = useGuiLaiLoiMoi();
  const mfa = useBatBuocMfaNguoiDung();
  const datLaiMatKhau = useGuiDatLaiMatKhau();
  const moKhoaDangNhap = useMoKhoaDangNhap();
  const datLaiMfa = useDatLaiMfa();
  const doiEmail = useDoiEmailNhanThu();
  const { t } = useT();

  const [hopMoi, setHopMoi] = useState({ mo: false, lan: 0 });
  const [hopSua, setHopSua] = useState<{ mo: boolean; lan: number; nguoi: UserProfile | null }>({
    mo: false,
    lan: 0,
    nguoi: null,
  });
  const [hopKhoa, setHopKhoa] = useState<{ mo: boolean; nguoi: UserProfile | null }>({
    mo: false,
    nguoi: null,
  });

  const [hopMfa, setHopMfa] = useState<{ mo: boolean; nguoi: UserProfile | null }>({
    mo: false,
    nguoi: null,
  });

  const [hopDatLai, setHopDatLai] = useState<{ mo: boolean; nguoi: UserProfile | null }>({
    mo: false,
    nguoi: null,
  });

  /** `batBuoc`: người đó đang bị bắt buộc xác thực hai lớp — thân hộp nói khác. */
  const [hopDatLaiMfa, setHopDatLaiMfa] = useState<{
    mo: boolean;
    nguoi: UserProfile | null;
    batBuoc: boolean;
  }>({ mo: false, nguoi: null, batBuoc: false });

  const [hopDoiEmail, setHopDoiEmail] = useState<{ mo: boolean; lan: number; nguoi: UserProfile | null }>({
    mo: false,
    lan: 0,
    nguoi: null,
  });

  const [nganVaiTro, setNganVaiTro] = useState<{ mo: boolean; lan: number; nguoi: UserProfile | null }>({
    mo: false,
    lan: 0,
    nguoi: null,
  });

  const dongKhoa = () => {
    setHopKhoa((h) => ({ ...h, mo: false }));
    khoa.reset();
  };

  const dongMfa = () => {
    setHopMfa((h) => ({ ...h, mo: false }));
    mfa.reset();
  };

  const dongDatLai = () => {
    setHopDatLai((h) => ({ ...h, mo: false }));
    datLaiMatKhau.reset();
  };

  const dongDatLaiMfa = () => {
    setHopDatLaiMfa((h) => ({ ...h, mo: false }));
    datLaiMfa.reset();
  };

  /** `409`/`429`/`503` của email đặt lại mật khẩu nói cách xử lý riêng; lỗi khác đi đường chung. */
  const loiDatLai = (() => {
    const err = datLaiMatKhau.error;
    if (!err) return null;
    const loai = phanLoaiLoiDatLai(err.status);
    return loai ? t(KHOA_LOI_DAT_LAI_MAT_KHAU[loai]) : dichLoi(err);
  })();

  /**
   * Mời xong mà gán vai trò hỏng: người dùng đã có, chỉ thiếu vai trò. Toast
   * cảnh báo giữ tới khi đóng, kèm nút gán lại; hỏng lần nữa thì báo lại.
   */
  const baoChuaGanVaiTro = (nguoi: InvitedUser, roleIds: string[], loi: ApiError) => {
    const khoaToast = `chua-gan-vai-tro-${nguoi.user_id}`;
    toast.warning(t('nguoiDung.chuaGanDuocVaiTro', { email: nguoi.email, loi: dichLoi(loi) }), {
      khoa: khoaToast,
      hanhDong: (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            toast.dong(khoaToast);
            ganLai.mutate(
              { userId: nguoi.user_id, roleIds },
              {
                onSuccess: () => toast.success(t('vaiTroLop2.daGanVaiTro', { ten: nguoi.display_name })),
                onError: (err) => baoChuaGanVaiTro(nguoi, roleIds, err),
              },
            );
          }}
        >
          {t('nguoiDung.ganLaiVaiTro')}
        </Button>
      ),
    });
  };

  const nguoiDatLai = hopDatLai.nguoi;
  const nguoiDatLaiMfa = hopDatLaiMfa.nguoi;
  const nguoiSua = hopSua.nguoi;
  const nguoiGan = nganVaiTro.nguoi;
  const nguoiDoiEmail = hopDoiEmail.nguoi;

  const hopThoai = (
    <>
      <FormMoiNguoiDung
        key={`moi-${hopMoi.lan}`}
        open={hopMoi.mo}
        dangGui={moi.isPending}
        onClose={() => setHopMoi((h) => ({ ...h, mo: false }))}
        onMoi={(du, khoaIdem, roleIds) =>
          moi.mutateAsync({ du, khoa: khoaIdem, roleIds }).then(({ moi: nguoiMoi, loiGanVaiTro }) => {
            setHopMoi((h) => ({ ...h, mo: false }));
            if (loiGanVaiTro) baoChuaGanVaiTro(nguoiMoi, roleIds, loiGanVaiTro);
          })
        }
      />
      {nguoiSua && (
        <FormSuaNguoiDung
          key={`sua-${hopSua.lan}`}
          nguoi={nguoiSua}
          open={hopSua.mo}
          dangLuu={sua.isPending}
          onClose={() => setHopSua((h) => ({ ...h, mo: false }))}
          onLuu={(du) =>
            sua
              .mutateAsync({ id: nguoiSua.user_id, du })
              .then(() => setHopSua((h) => ({ ...h, mo: false })))
          }
        />
      )}
      {nguoiGan && (
        <NganGanVaiTro
          key={`vai-tro-${nganVaiTro.lan}`}
          nguoi={nguoiGan}
          open={nganVaiTro.mo}
          onClose={() => setNganVaiTro((h) => ({ ...h, mo: false }))}
        />
      )}
      {nguoiDoiEmail && (
        <HopDoiEmailNhanThu
          key={`doi-email-${hopDoiEmail.lan}`}
          nguoi={nguoiDoiEmail}
          open={hopDoiEmail.mo}
          dangChay={doiEmail.isPending}
          onClose={() => setHopDoiEmail((h) => ({ ...h, mo: false }))}
          onDoi={(email) =>
            doiEmail
              .mutateAsync({ nguoi: nguoiDoiEmail, email })
              .then(() => setHopDoiEmail((h) => ({ ...h, mo: false })))
          }
        />
      )}
      <HopKhoaNguoiDung
        open={hopKhoa.mo}
        nguoi={hopKhoa.nguoi}
        dangChay={khoa.isPending}
        loi={khoa.error}
        onClose={dongKhoa}
        onXacNhan={(nguoi) =>
          khoa.mutate(
            { id: nguoi.user_id, hanhDong: 'khoa' },
            { onSuccess: () => setHopKhoa((h) => ({ ...h, mo: false })) },
          )
        }
      />
      <HopBatBuocMfa
        open={hopMfa.mo}
        nguoi={hopMfa.nguoi}
        dangChay={mfa.isPending}
        loi={mfa.error}
        onClose={dongMfa}
        onXacNhan={(nguoi) =>
          mfa.mutate(
            { id: nguoi.user_id, batBuoc: true },
            { onSuccess: () => setHopMfa((h) => ({ ...h, mo: false })) },
          )
        }
      />
      <HopXacNhanThaoTac
        open={hopDatLai.mo}
        tieuDe={t('nguoiDung.layLai.xnDatLaiTieuDe', { ten: nguoiDatLai?.display_name ?? '' })}
        than={t('nguoiDung.layLai.xnDatLaiThan', { email: (nguoiDatLai && noiNhanThu(nguoiDatLai)) ?? '' })}
        nhanXacNhan={t('nguoiDung.layLai.guiDatLaiMatKhau')}
        dangChay={datLaiMatKhau.isPending}
        loi={loiDatLai}
        onClose={dongDatLai}
        onXacNhan={() =>
          nguoiDatLai &&
          datLaiMatKhau.mutate(nguoiDatLai, {
            onSuccess: () => setHopDatLai((h) => ({ ...h, mo: false })),
          })
        }
      />
      <HopXacNhanThaoTac
        open={hopDatLaiMfa.mo}
        tieuDe={t('nguoiDung.layLai.xnMfaTieuDe', { ten: nguoiDatLaiMfa?.display_name ?? '' })}
        than={t(hopDatLaiMfa.batBuoc ? 'nguoiDung.layLai.xnMfaThanBatBuoc' : 'nguoiDung.layLai.xnMfaThan')}
        nhanXacNhan={t('nguoiDung.layLai.datLaiMfa')}
        nguyHiem
        dangChay={datLaiMfa.isPending}
        loi={datLaiMfa.error ? dichLoi(datLaiMfa.error) : null}
        onClose={dongDatLaiMfa}
        onXacNhan={() =>
          nguoiDatLaiMfa &&
          datLaiMfa.mutate(nguoiDatLaiMfa, {
            onSuccess: () => setHopDatLaiMfa((h) => ({ ...h, mo: false })),
          })
        }
      />
    </>
  );

  return {
    moMoi: () => setHopMoi((h) => ({ mo: true, lan: h.lan + 1 })),
    moSua: (nguoi: UserProfile) => setHopSua((h) => ({ mo: true, lan: h.lan + 1, nguoi })),
    moKhoa: (nguoi: UserProfile) => {
      // Lỗi của lần trước (có thể của người khác) không được hiện ở hộp mới.
      khoa.reset();
      setHopKhoa({ mo: true, nguoi });
    },
    /** Mở khoá không phá huỷ gì nên không hỏi lại; lỗi đi bằng toast vì không có hộp nào. */
    moKhoaNgay: (nguoi: UserProfile) =>
      khoa.mutate(
        { id: nguoi.user_id, hanhDong: 'mo-khoa' },
        { onError: (err) => toast.error(dichLoi(err)) },
      ),
    guiLaiLoiMoi: (nguoi: UserProfile) => guiLai.mutate(nguoi),
    /** Bắt buộc xác thực hai lớp: người đó bị đăng xuất, nên hỏi lại. */
    moBatBuocMfa: (nguoi: UserProfile) => {
      mfa.reset();
      setHopMfa({ mo: true, nguoi });
    },
    /** Bỏ bắt buộc không đăng xuất ai nên không hỏi lại; lỗi đi bằng toast. */
    boBatBuocMfaNgay: (nguoi: UserProfile) =>
      mfa.mutate(
        { id: nguoi.user_id, batBuoc: false },
        { onError: (err) => toast.error(dichLoi(err)) },
      ),
    /** Email đặt lại mật khẩu: gửi thư tới người khác, nên hỏi lại và nói rõ địa chỉ nhận. */
    moDatLaiMatKhau: (nguoi: UserProfile) => {
      datLaiMatKhau.reset();
      setHopDatLai({ mo: true, nguoi });
    },
    /** Mở khoá đăng nhập không phá huỷ gì nên không hỏi lại; lỗi đi bằng toast. */
    moKhoaDangNhapNgay: (nguoi: UserProfile) =>
      moKhoaDangNhap.mutate(nguoi, { onError: (err) => toast.error(dichLoi(err)) }),
    /** Đặt lại xác thực hai lớp: xoá thiết bị và đăng xuất người đó, không hoàn tác — hỏi lại. */
    moDatLaiMfa: (nguoi: UserProfile, batBuoc: boolean) => {
      datLaiMfa.reset();
      setHopDatLaiMfa({ mo: true, nguoi, batBuoc });
    },
    /** Đổi email nhận thư (ADR-0101): thư đặt lại mật khẩu đổi địa chỉ, nên hỏi lại kèm hậu quả. */
    moDoiEmailNhanThu: (nguoi: UserProfile) => {
      doiEmail.reset();
      setHopDoiEmail((h) => ({ mo: true, lan: h.lan + 1, nguoi }));
    },
    /** Ngăn gán vai trò lớp 2 — dựng lại sạch mỗi lần mở, đọc lại `ETag` mới. */
    moGanVaiTro: (nguoi: UserProfile) => setNganVaiTro((h) => ({ mo: true, lan: h.lan + 1, nguoi })),
    /** Dòng đang chạy thao tác bấm thẳng (gửi lại, mở khoá) — để khoá nút tránh bấm hai lần. */
    dangChayVoi: (id: string) =>
      (guiLai.isPending && guiLai.variables?.user_id === id) ||
      (khoa.isPending && khoa.variables?.hanhDong === 'mo-khoa' && khoa.variables.id === id) ||
      (mfa.isPending && mfa.variables?.batBuoc === false && mfa.variables.id === id) ||
      (moKhoaDangNhap.isPending && moKhoaDangNhap.variables?.user_id === id),
    hopThoai,
  };
}
